import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight,
  Check,
  X,
  Sparkles,
  HelpCircle,
  Users,
  Layers,
  Globe,
  BookOpen,
  Webhook,
  Code,
  Shield,
  Zap,
  Star
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import MorphingNavbar from '../../components/MorphingNavbar';

// ============================================
// PRICING DATA
// ============================================

const PRICING_TIERS = [
  {
    name: 'Starter',
    description: 'Perfect for freelancers and small agencies just getting started.',
    monthlyPrice: 39,
    yearlyPrice: 390,
    yearlyDiscount: '2 months free',
    highlighted: false,
    features: {
      clients: 100,
      services: 50,
      teamMembers: 5,
      customDomain: 'Subdomain only',
      knowledgeBase: '10 articles',
      webhooks: 0,
      apiAccess: 'None',
      removeBranding: false,
      platformFee: '5%'
    },
    cta: 'Start free trial',
    ctaLink: '/register?plan=starter'
  },
  {
    name: 'Growth',
    description: 'For growing agencies that need more power and flexibility.',
    monthlyPrice: 79,
    yearlyPrice: 790,
    yearlyDiscount: '2 months free',
    highlighted: true,
    badge: 'Most Popular',
    features: {
      clients: 300,
      services: 150,
      teamMembers: 10,
      customDomain: '1 custom domain',
      knowledgeBase: '50 articles',
      webhooks: 5,
      apiAccess: 'Read-only',
      removeBranding: false,
      platformFee: '3%'
    },
    cta: 'Start free trial',
    ctaLink: '/register?plan=growth'
  },
  {
    name: 'Scale',
    description: 'For established agencies that demand the best.',
    monthlyPrice: 149,
    yearlyPrice: 1490,
    yearlyDiscount: '2 months free',
    highlighted: false,
    features: {
      clients: 500,
      services: 300,
      teamMembers: 20,
      customDomain: '3 custom domains',
      knowledgeBase: '200 articles',
      webhooks: 25,
      apiAccess: 'Full access',
      removeBranding: true,
      platformFee: '2%'
    },
    cta: 'Start free trial',
    ctaLink: '/register?plan=scale'
  }
];

const APPSUMO_TIERS = [
  {
    name: 'AppSumo Tier 1',
    price: 69,
    originalValue: 468,
    platformFee: '5%',
    features: PRICING_TIERS[0].features
  },
  {
    name: 'AppSumo Tier 2',
    price: 139,
    originalValue: 948,
    platformFee: '3%',
    features: PRICING_TIERS[1].features
  },
  {
    name: 'AppSumo Tier 3',
    price: 279,
    originalValue: 1788,
    platformFee: '2%',
    features: PRICING_TIERS[2].features
  }
];

const FEATURE_COMPARISON = [
  {
    category: 'Core Limits',
    features: [
      { name: 'Clients', starter: '100', growth: '300', scale: '500' },
      { name: 'Services', starter: '50', growth: '150', scale: '300' },
      { name: 'Team Members', starter: '5', growth: '10', scale: '20' },
      { name: 'Knowledge Base Articles', starter: '10', growth: '50', scale: '200' }
    ]
  },
  {
    category: 'Branding & Domains',
    features: [
      { name: 'Custom Domain', starter: false, growth: '1 domain', scale: '3 domains' },
      { name: 'Custom Branding', starter: true, growth: true, scale: true },
      { name: 'Remove Edrano Badge', starter: false, growth: false, scale: true },
      { name: 'Custom Email Domain', starter: false, growth: true, scale: true }
    ]
  },
  {
    category: 'Integrations',
    features: [
      { name: 'Stripe Payments', starter: true, growth: true, scale: true },
      { name: 'Webhooks', starter: '0', growth: '5', scale: '25' },
      { name: 'API Access', starter: false, growth: 'Read-only', scale: 'Full' },
      { name: 'Zapier Integration', starter: false, growth: true, scale: true }
    ]
  },
  {
    category: 'AI Features',
    features: [
      { name: 'AI Chat Assistant', starter: true, growth: true, scale: true },
      { name: 'Lead Capture', starter: true, growth: true, scale: true },
      { name: 'AI Credits (monthly)', starter: '100', growth: '500', scale: '2000' },
      { name: 'Calendar Integration', starter: false, growth: true, scale: true }
    ]
  },
  {
    category: 'Support',
    features: [
      { name: 'Email Support', starter: true, growth: true, scale: true },
      { name: 'Priority Support', starter: false, growth: true, scale: true },
      { name: 'Onboarding Call', starter: false, growth: false, scale: true },
      { name: 'Dedicated Account Manager', starter: false, growth: false, scale: true }
    ]
  }
];

const FAQS = [
  {
    q: 'Is there a free trial?',
    a: 'Yes! All plans come with a 5-day free trial. No credit card required to start. You can explore all features and decide if Edrano is right for you.'
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, American Express) through Stripe. We also support PayPal and bank transfers for annual plans.'
  },
  {
    q: 'Can I change plans later?',
    a: 'Absolutely! You can upgrade or downgrade your plan at any time. When upgrading, you\'ll be charged a prorated amount. When downgrading, the credit will apply to future invoices.'
  },
  {
    q: 'What happens when I hit my limits?',
    a: 'We\'ll notify you at 80% usage. When you reach 100%, you can still access existing data but won\'t be able to add new items. Upgrade anytime to increase limits.'
  },
  {
    q: 'What\'s the platform fee?',
    a: 'The platform fee applies to payments processed through your portal. Starter pays 5%, Growth pays 3%, and Scale pays 2%. This is on top of standard Stripe fees (2.9% + $0.30).'
  },
  {
    q: 'Do you offer refunds?',
    a: 'We offer a 30-day money-back guarantee on all plans. If you\'re not satisfied, contact us within 30 days of your purchase for a full refund.'
  },
  {
    q: 'What\'s included in AppSumo deals?',
    a: 'AppSumo deals are lifetime deals—pay once, use forever. They include all features of the corresponding tier but with a one-time platform fee on transactions.'
  },
  {
    q: 'Can I white-label completely?',
    a: 'Scale plan users can remove all Edrano branding. Your clients will see only your brand on the portal, emails, and everywhere else.'
  }
];

// ============================================
// COMPONENTS
// ============================================

function PricingCard({ tier, isYearly }) {
  const price = isYearly ? tier.yearlyPrice : tier.monthlyPrice;
  const period = isYearly ? '/year' : '/month';
  
  return (
    <div className={`relative rounded-2xl p-8 ${
      tier.highlighted 
        ? 'bg-stone-900 text-white ring-4 ring-orange-500 ring-offset-4' 
        : 'bg-white border-2 border-stone-200'
    }`}>
      {tier.badge && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2">
          <div className="px-4 py-1 bg-gradient-to-r from-orange-500 to-pink-500 text-white text-sm font-medium rounded-full">
            {tier.badge}
          </div>
        </div>
      )}
      
      <div className="text-center mb-6">
        <h3 className={`text-xl font-bold mb-2 ${tier.highlighted ? 'text-white' : 'text-stone-900'}`}>
          {tier.name}
        </h3>
        <p className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-500'}`}>
          {tier.description}
        </p>
      </div>
      
      <div className="text-center mb-6">
        <div className="flex items-baseline justify-center gap-1">
          <span className={`text-4xl font-bold ${tier.highlighted ? 'text-white' : 'text-stone-900'}`}>
            ${price}
          </span>
          <span className={tier.highlighted ? 'text-stone-400' : 'text-stone-500'}>
            {period}
          </span>
        </div>
        {isYearly && (
          <div className="mt-1 text-sm text-emerald-500 font-medium">
            {tier.yearlyDiscount}
          </div>
        )}
        <div className={`mt-2 text-xs ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`}>
          + {tier.features.platformFee} platform fee on transactions
        </div>
      </div>
      
      <div className="space-y-3 mb-8">
        <div className="flex items-center gap-3">
          <Users className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.clients} clients
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Layers className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.services} services
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Users className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.teamMembers} team members
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Globe className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.customDomain}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <BookOpen className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.knowledgeBase}
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Webhook className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.webhooks} webhooks
          </span>
        </div>
        <div className="flex items-center gap-3">
          <Code className={`w-4 h-4 ${tier.highlighted ? 'text-stone-400' : 'text-stone-500'}`} />
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            {tier.features.apiAccess || 'No'} API access
          </span>
        </div>
        <div className="flex items-center gap-3">
          {tier.features.removeBranding ? (
            <Check className="w-4 h-4 text-emerald-500" />
          ) : (
            <X className={`w-4 h-4 ${tier.highlighted ? 'text-stone-500' : 'text-stone-400'}`} />
          )}
          <span className={`text-sm ${tier.highlighted ? 'text-stone-300' : 'text-stone-600'}`}>
            Remove Edrano branding
          </span>
        </div>
      </div>
      
      <Link to={tier.ctaLink}>
        <Button className={`w-full ${
          tier.highlighted 
            ? 'bg-white text-stone-900 hover:bg-stone-100' 
            : 'bg-stone-900 text-white hover:bg-stone-800'
        }`}>
          {tier.cta}
          <ArrowRight className="w-4 h-4 ml-2" />
        </Button>
      </Link>
    </div>
  );
}

function FeatureValue({ value }) {
  if (value === true) return <Check className="w-5 h-5 text-emerald-500 mx-auto" />;
  if (value === false) return <X className="w-5 h-5 text-stone-300 mx-auto" />;
  return <span className="text-stone-700">{value}</span>;
}

function FAQItem({ faq, isOpen, onToggle }) {
  return (
    <div className="border-b border-stone-200">
      <button 
        className="w-full py-5 flex items-center justify-between text-left"
        onClick={onToggle}
      >
        <span className="font-medium text-stone-900">{faq.q}</span>
        <div className={`transform transition-transform ${isOpen ? 'rotate-45' : ''}`}>
          <span className="text-2xl text-stone-400">+</span>
        </div>
      </button>
      {isOpen && (
        <div className="pb-5 text-stone-600 leading-relaxed">
          {faq.a}
        </div>
      )}
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-stone-900 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div>
            <div className="flex items-center gap-2 mb-4">
              <img src="/edrano-logo-dark.png" alt="Edrano" className="w-8 h-8 rounded-lg" />
              <span className="font-semibold">Edrano</span>
            </div>
            <p className="text-stone-400 text-sm">The client portal for service agencies.</p>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Product</h4>
            <div className="space-y-2">
              <Link to="/features" className="block text-stone-400 hover:text-white text-sm">Features</Link>
              <Link to="/pricing" className="block text-stone-400 hover:text-white text-sm">Pricing</Link>
              <Link to="/use-cases" className="block text-stone-400 hover:text-white text-sm">Use Cases</Link>
              <Link to="/compare" className="block text-stone-400 hover:text-white text-sm">Compare</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Resources</h4>
            <div className="space-y-2">
              <Link to="/docs" className="block text-stone-400 hover:text-white text-sm">Documentation</Link>
              <Link to="/docs/quick-start" className="block text-stone-400 hover:text-white text-sm">Getting Started</Link>
            </div>
          </div>
          <div>
            <h4 className="font-semibold mb-4">Legal</h4>
            <div className="space-y-2">
              <Link to="/privacy" className="block text-stone-400 hover:text-white text-sm">Privacy</Link>
              <Link to="/terms" className="block text-stone-400 hover:text-white text-sm">Terms</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-stone-800 pt-8 text-center text-stone-500 text-sm">
          © {new Date().getFullYear()} Edrano. All rights reserved.
          <span className="mx-2">•</span>
          Part of <a href="https://themelio.app" target="_blank" rel="noopener noreferrer" className="text-orange-400 hover:underline">Themelio</a>
        </div>
      </div>
    </footer>
  );
}

// ============================================
// MAIN COMPONENT
// ============================================

export default function Pricing() {
  const [isYearly, setIsYearly] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(0);

  return (
    <div className="min-h-screen bg-stone-50">
      <MorphingNavbar />
      
      {/* Hero */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-stone-900 mb-4">
            Simple, transparent pricing
          </h1>
          <p className="text-xl text-stone-600 max-w-2xl mx-auto mb-8">
            Start free for 5 days. No credit card required. Choose the plan that fits your agency.
          </p>
          
          {/* Toggle */}
          <div className="inline-flex items-center gap-3 p-1 bg-stone-100 rounded-full mb-12">
            <button
              onClick={() => setIsYearly(false)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                !isYearly ? 'bg-white text-stone-900 shadow' : 'text-stone-600'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setIsYearly(true)}
              className={`px-6 py-2 rounded-full text-sm font-medium transition-all ${
                isYearly ? 'bg-white text-stone-900 shadow' : 'text-stone-600'
              }`}
            >
              Yearly
              <span className="ml-2 text-xs text-emerald-600 font-semibold">Save 17%</span>
            </button>
          </div>
        </div>
      </section>

      {/* Pricing Cards */}
      <section className="py-12 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8">
            {PRICING_TIERS.map((tier) => (
              <PricingCard key={tier.name} tier={tier} isYearly={isYearly} />
            ))}
          </div>
        </div>
      </section>

      {/* AppSumo Section */}
      <section className="py-16 bg-gradient-to-r from-amber-500 to-orange-500">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/20 backdrop-blur rounded-full text-white text-sm mb-4">
              <Star className="w-4 h-4" />
              Limited Time Offer
            </div>
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
              AppSumo Lifetime Deals
            </h2>
            <p className="text-lg text-white/90 max-w-2xl mx-auto">
              Pay once, use forever. Get all the features of our subscription plans with a one-time payment.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-6">
            {APPSUMO_TIERS.map((tier) => (
              <div key={tier.name} className="bg-white rounded-xl p-6 shadow-xl">
                <h3 className="font-bold text-lg text-stone-900 mb-2">{tier.name}</h3>
                <div className="flex items-baseline gap-2 mb-4">
                  <span className="text-3xl font-bold text-stone-900">${tier.price}</span>
                  <span className="text-sm text-stone-400 line-through">${tier.originalValue}/yr value</span>
                </div>
                <div className="text-sm text-stone-500 mb-4">+ {tier.platformFee} platform fee</div>
                <Link to="/appsumo">
                  <Button className="w-full bg-amber-500 hover:bg-amber-600">
                    Redeem Code
                  </Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Feature Comparison Table */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">Compare all features</h2>
            <p className="text-stone-600">See exactly what you get with each plan</p>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-stone-200">
                  <th className="text-left py-4 px-4 font-medium text-stone-500">Feature</th>
                  <th className="text-center py-4 px-4 font-semibold text-stone-900">Starter</th>
                  <th className="text-center py-4 px-4 font-semibold text-stone-900 bg-orange-50">Growth</th>
                  <th className="text-center py-4 px-4 font-semibold text-stone-900">Scale</th>
                </tr>
              </thead>
              <tbody>
                {FEATURE_COMPARISON.map((category) => (
                  <React.Fragment key={category.category}>
                    <tr className="bg-stone-50">
                      <td colSpan={4} className="py-3 px-4 font-semibold text-stone-900">
                        {category.category}
                      </td>
                    </tr>
                    {category.features.map((feature) => (
                      <tr key={feature.name} className="border-b border-stone-100">
                        <td className="py-3 px-4 text-stone-600">{feature.name}</td>
                        <td className="py-3 px-4 text-center"><FeatureValue value={feature.starter} /></td>
                        <td className="py-3 px-4 text-center bg-orange-50/50"><FeatureValue value={feature.growth} /></td>
                        <td className="py-3 px-4 text-center"><FeatureValue value={feature.scale} /></td>
                      </tr>
                    ))}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-stone-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">Frequently asked questions</h2>
            <p className="text-stone-600">Everything you need to know about pricing</p>
          </div>
          
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 overflow-hidden">
            <div className="divide-y divide-stone-200 px-6">
              {FAQS.map((faq, index) => (
                <FAQItem 
                  key={index}
                  faq={faq}
                  isOpen={openFAQ === index}
                  onToggle={() => setOpenFAQ(openFAQ === index ? -1 : index)}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-stone-900">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Ready to get started?
          </h2>
          <p className="text-lg text-stone-300 mb-8">
            Start your 5-day free trial today. No credit card required.
          </p>
          <Link to="/register">
            <Button size="lg" className="bg-white text-stone-900 hover:bg-stone-100">
              Start free trial
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
